import React, { RefObject, forwardRef, useContext } from 'react';
import { Box, Grid } from '@mui/material';
import {
  MediumCard,
  CardTitle,
  CardValue,
  CardRow,
  AnalysisRow,
  PillText,
} from 'spw-programming-projects/program-summary/single-page-summary/styles';
import { numberWithCommasOrDash } from 'spw-shared/utils';
import { NO_DATA_TO_SHOW } from 'spw-constants';
import {
  CardSmallValue,
  CardValueTitle,
  Pill,
} from 'spw-programming-projects/program-summary/components/styles';
import CardIcon from 'spw-esa-projects/program-summary/card-icon';
import { useCardIcon } from 'spw-esa-projects/program-summary/cards/hooks';
import { ProgramSummaryCardType } from 'spw-esa-projects/program-summary/icon-selector-popover';
import { useDialogStatus } from 'spw-components/dialogs/total-program/use-dialog-status';
import { SEAT_COUNT_VALUES } from 'spw-components/dialogs/total-program/utils';
import { SeatCountAnalysisDialog } from 'spw-components/dialogs/total-program/seat-count-analysis-dialog';
import { MarginLeft4 } from 'spw-shared/globalComponents/containers';
import { IconWrapper } from '../macro-single-page-summary/styles';
import { PlainWorksheet } from '../../../../models/Worksheet';
import { ProgrammingSummaryCardsContext } from '../program-summary-tabs/summary-card-context';

interface ISeatCountAnalysisProps {
  futureWorkSeats: number;
  futureHeadcount: number;
  deskSharingRatio: number;
  isTotalProgramScenario: boolean;
  totalProgramWorksheets?: PlainWorksheet[];
}

const SeatCountAnalysis = forwardRef<HTMLElement, ISeatCountAnalysisProps>(
  (
    {
      futureWorkSeats,
      futureHeadcount,
      deskSharingRatio,
      isTotalProgramScenario,
      totalProgramWorksheets,
    },
    ref
  ) => {
    const {
      showDialog,
      openDialog,
      valueType,
      dialogValue,
      setShowDialog,
    } = useDialogStatus(isTotalProgramScenario);

    const cardIcon = useCardIcon(ProgramSummaryCardType.seatCountAnalysis);
    const { selectedCardIcon, onClickCardIcon } = useContext(
      ProgrammingSummaryCardsContext
    );

    const handleIconClick = (event: React.MouseEvent<HTMLDivElement>) => {
      onClickCardIcon(event, cardIcon);
    };

    const isIconCardSelected =
      selectedCardIcon?.cardType === ProgramSummaryCardType.seatCountAnalysis;

    if (!cardIcon) return null;

    const deskSharingRatioValue = deskSharingRatio
      ? `1: ${deskSharingRatio.toFixed(2)}`
      : NO_DATA_TO_SHOW;

    const showFutureWorkSeats = () => {
      openDialog(SEAT_COUNT_VALUES.FUTURE_WORK_SEATS, futureWorkSeats);
    };

    const showDeskSharingRatio = () => {
      openDialog(SEAT_COUNT_VALUES.DESK_SHARING_RATIO, deskSharingRatio);
    };

    return (
      <>
        {showDialog && isTotalProgramScenario && !window.SPWExport && (
          <SeatCountAnalysisDialog
            anchorRef={ref}
            totalProgramWorksheets={totalProgramWorksheets}
            showDialog={showDialog}
            setShowDialog={setShowDialog}
            value={dialogValue}
            valueType={valueType}
            showFutureWorkSeats={showFutureWorkSeats}
            showDeskSharingRatio={showDeskSharingRatio}
          />
        )}
        <div ref={ref as RefObject<HTMLDivElement>}>
          <MediumCard>
            <CardTitle
              className="mb-8"
              data-testid="programming_ps_seatcount_analysis_title"
            >
              SEAT COUNT ANALYSIS
            </CardTitle>
            <Grid container>
              <Grid item xs={12}>
                <AnalysisRow display="flex">
                  <IconWrapper>
                    <CardIcon
                      iconPath={`/images/space-icons/${cardIcon.iconPath}`}
                      onClick={handleIconClick}
                      height={40}
                      isSelected={isIconCardSelected}
                    />
                  </IconWrapper>
                  <Box
                    flex={1}
                    onClick={showFutureWorkSeats}
                    className={`seatcount ${
                      isTotalProgramScenario ? 'highlight' : ''
                    }`}
                  >
                    <CardValue data-testid="programming_ps_seatcount_analysis_workseats">
                      {numberWithCommasOrDash(futureWorkSeats)}
                    </CardValue>
                    <CardValueTitle data-testid="programming_ps_seatcount_analysis_workseats_label">
                      Work Seats
                    </CardValueTitle>
                  </Box>
                </AnalysisRow>
              </Grid>

              <Grid item xs={12}>
                <Box>
                  <CardRow display="flex">
                    <Box display="flex">
                      <CardSmallValue data-testid="programming_ps_seatcount_analysis_headcountWithUtilization">
                        {numberWithCommasOrDash(futureHeadcount)}{' '}
                      </CardSmallValue>
                      <MarginLeft4 data-testid="programming_ps_seatcount_analysis_headcountWithUtilization_label">
                        Headcount /
                      </MarginLeft4>
                    </Box>
                  </CardRow>
                  <Pill
                    style={{ paddingLeft: 0 }}
                    color="transparent"
                    className={`${isTotalProgramScenario ? 'highlight' : ''}`}
                    onClick={showDeskSharingRatio}
                  >
                    <CardSmallValue data-testid="programming_ps_seatcount_analysis_deskSharingRatio">
                      {deskSharingRatioValue}
                    </CardSmallValue>
                    <PillText data-testid="programming_ps_seatcount_analysis_deskSharingRatio_label">
                      Desk Sharing Ratio
                    </PillText>
                  </Pill>
                </Box>
              </Grid>
            </Grid>
          </MediumCard>
        </div>
      </>
    );
  }
);

const MemoizedSeatCountAnalysis = React.memo(SeatCountAnalysis);

MemoizedSeatCountAnalysis.whyDidYouRender = true;

export { MemoizedSeatCountAnalysis as SeatCountAnalysis };
